import React, { useEffect, useState } from "react";

interface MobileAppRedirectProps {
  showBanner?: boolean;
}

const MobileAppRedirect: React.FC<MobileAppRedirectProps> = ({
  showBanner = true,
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [showBannerState, setShowBannerState] = useState(showBanner);

  useEffect(() => {
    // Detect if user is on mobile
    const checkMobile = () => {
      const userAgent =
        navigator.userAgent || navigator.vendor || (window as any).opera;
      const isMobileDevice =
        /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
          userAgent.toLowerCase()
        );
      setIsMobile(isMobileDevice);

      // Show popup if on mobile and coming from email verification
      const urlParams = new URLSearchParams(window.location.search);
      const fromEmail =
        urlParams.get("type") === "signup" || urlParams.get("type") === "email";

      if (isMobileDevice && fromEmail) {
        // Small delay to ensure page is loaded
        setTimeout(() => setShowPopup(true), 500);
      }
    };

    checkMobile();
  }, []);

  const handleOpenApp = () => {
    // Deep link to open the app
    // iOS: amblyfy://
    // Android: amblyfy://
    const deepLink = "amblyfy://login";

    // Try to open the app
    window.location.href = deepLink;

    // If app doesn't open after 2 seconds, redirect to store
    setTimeout(() => {
      handleDownloadApp();
    }, 2000);
  };

  const handleDownloadApp = () => {
    const userAgent =
      navigator.userAgent || navigator.vendor || (window as any).opera;

    if (/iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream) {
      // iOS - redirect to App Store
      window.location.href =
        "https://apps.apple.com/lv/app/amblyfy/id6751319730";
    } else if (/android/i.test(userAgent)) {
      // Android - Coming soon
      alert("Amblyfy for Android is coming soon! Stay tuned for updates.");
    }
  };

  const closeBanner = () => {
    setShowBannerState(false);
    localStorage.setItem("hideAppBanner", "true");
  };

  const closePopup = () => {
    setShowPopup(false);
  };

  // Check if user previously dismissed banner
  useEffect(() => {
    const hideBanner = localStorage.getItem("hideAppBanner");
    if (hideBanner === "true") {
      setShowBannerState(false);
    }
  }, []);

  return (
    <>
      {/* Mobile App Banner */}
      {isMobile && showBannerState && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center space-x-3 flex-1">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                <span className="text-2xl">👁️</span>
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold">Get the Amblyfy App</p>
                <p className="text-xs opacity-90">
                  Better experience on mobile
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleDownloadApp}
                className="px-4 py-2 bg-white text-cyan-600 rounded-lg text-sm font-semibold hover:bg-gray-100 transition-colors"
              >
                Download
              </button>
              <button
                onClick={closeBanner}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                aria-label="Close banner"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile App Popup */}
      {showPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md mx-4 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-cyan-500 to-blue-600 p-6 text-white text-center">
              <div className="w-20 h-20 bg-white rounded-2xl mx-auto mb-4 flex items-center justify-center shadow-lg">
                <span className="text-5xl">👁️</span>
              </div>
              <h2 className="text-2xl font-bold mb-2">Welcome to Amblyfy!</h2>
              <p className="text-sm opacity-90">
                Get the best experience with our mobile app
              </p>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              <div className="space-y-3">
                <button
                  onClick={handleOpenApp}
                  className="w-full py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl font-semibold text-lg hover:shadow-lg transition-all transform hover:scale-105"
                >
                  Open in Amblyfy App
                </button>

                <button
                  onClick={handleDownloadApp}
                  className="w-full py-4 bg-gray-100 text-gray-800 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
                >
                  Download App
                </button>

                <button
                  onClick={closePopup}
                  className="w-full py-3 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Continue in Browser
                </button>
              </div>

              {/* App Store Badges */}
              <div className="pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-500 text-center mb-3">
                  Available on
                </p>
                <div className="flex justify-center space-x-3">
                  <button
                    onClick={() => {
                      window.location.href =
                        "https://apps.apple.com/lv/app/amblyfy/id6751319730";
                    }}
                    className="hover:opacity-80 transition-opacity"
                  >
                    <img
                      src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83"
                      alt="Download on the App Store"
                      className="h-10"
                    />
                  </button>
                  <div className="relative">
                    <button
                      onClick={() => {
                        alert(
                          "Amblyfy for Android is coming soon! Stay tuned for updates."
                        );
                      }}
                      className="hover:opacity-80 transition-opacity relative"
                    >
                      <img
                        src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                        alt="Get it on Google Play"
                        className="h-10 opacity-50"
                      />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="bg-gray-800 text-white text-xs px-2 py-1 rounded font-semibold">
                          Coming Soon
                        </span>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MobileAppRedirect;
